import { SERIES_BG, type SeriesColour } from './scale';
import { cn } from '@/util/utils';

export interface BarListItem {
    label: string;
    value: number;
}

export interface BarListProps {
    items: BarListItem[];
    format: (value: number) => string;
    /** Number the rows, as a ranking does (.rp-rank); a breakdown leaves them bare (.rp-hbar). */
    ranked?: boolean;
    colour?: SeriesColour;
    /** Said in place of the list when it has nothing in it. */
    emptyNote: string;
    className?: string;
}

/**
 * Horizontal bars, one per row, each as long as its share of the largest: the Reports ranked
 * list and its horizontal breakdown. A list, not a picture, so a screen reader reads each row's
 * name and figure as they stand and needs no table beside it.
 */
export function BarList({
    items,
    format,
    ranked = false,
    colour = 0,
    emptyNote,
    className,
}: BarListProps) {
    if (items.length === 0) {
        return <p className="text-muted-foreground py-4 text-sm">{emptyNote}</p>;
    }
    const max = Math.max(...items.map((item) => item.value));

    return (
        <ol className={cn('flex min-w-0 flex-col', className)}>
            {items.map((item, i) => (
                <li
                    // Two clients may share a name; the place in the list is what tells them apart.
                    key={`${i}-${item.label}`}
                    className="border-border flex items-center gap-3 border-b py-2.5 last:border-b-0"
                >
                    {ranked ? (
                        <span className="text-label-md text-muted-foreground w-[18px] shrink-0">
                            {i + 1}
                        </span>
                    ) : null}
                    <span className="w-[42%] max-w-[150px] min-w-0 shrink-0 truncate text-sm font-medium">
                        {item.label}
                    </span>
                    <span
                        aria-hidden
                        className="bg-muted h-2 min-w-0 flex-1 overflow-hidden rounded-full"
                    >
                        <i
                            data-bar={item.label}
                            className={cn('block h-full rounded-full', SERIES_BG[colour])}
                            style={{
                                width: `${max > 0 ? Math.round((item.value / max) * 100) : 0}%`,
                            }}
                        />
                    </span>
                    <span className="w-16 shrink-0 text-right text-sm font-medium tabular-nums">
                        {format(item.value)}
                    </span>
                </li>
            ))}
        </ol>
    );
}
